import { supabase } from '../services/supabase'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
// Always use current origin so invite/reset links work on any domain (sfpcrm.com, localhost, etc.)
const APP_URL = window.location.origin

/**
 * Hook for admin authentication operations.
 * Calls Edge Functions that require service role access.
 */
export function useAuthAdmin() {
  /**
   * Get the current session's access token
   */
  const getAccessToken = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    return session?.access_token
  }

  /**
   * Invite a new user by email via Edge Function
   * Creates auth account + sends invite email
   */
  const inviteUser = async ({ email, full_name, advisor_id }) => {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')

    const redirectTo = `${APP_URL}/auth/reset-password`

    const response = await fetch(`${SUPABASE_URL}/functions/v1/invite-user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        email,
        full_name,
        advisor_id,
        redirect_to: redirectTo,
      }),
    })

    const data = await response.json()
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(data.error || 'Email rate limit exceeded. Please wait a few minutes and try again.')
      }
      throw new Error(data.error || `Failed to invite user (${response.status})`)
    }
    return data
  }

  /**
   * Update a user's email in Supabase Auth
   */
  const updateUserEmail = async ({ old_email, new_email, advisor_id }) => {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/functions/v1/update-user-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        old_email,
        new_email,
        advisor_id,
      }),
    })

    const data = await response.json()
    if (!response.ok) {
      if (response.status === 429) {
        throw new Error(data.error || 'Email rate limit exceeded. Please wait a few minutes and try again.')
      }
      throw new Error(data.error || `Failed to update email (${response.status})`)
    }
    return data
  }

  /**
   * Send a password reset email to a user via Edge Function (Resend, no OTP rate limit)
   */
  const sendPasswordReset = async (email) => {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/functions/v1/send-password-reset`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email }),
    })

    const data = await response.json()
    if (!response.ok) {
      throw new Error(data.error || `Failed to send password reset (${response.status})`)
    }
    return data
  }

  /**
   * Generate a magic sign-in link for a user (no email required, no OTP rate limit)
   * Returns the link as a string that can be converted to QR code
   * Points to signup page where new users can set their password
   */
  const generateSignInLink = async (email) => {
    const token = await getAccessToken()
    if (!token) throw new Error('Not authenticated')

    // Use configured app URL, fallback to current origin for local dev
    const redirectTo = `${APP_URL}/auth/reset-password`

    const response = await fetch(`${SUPABASE_URL}/functions/v1/generate-sign-in-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email, redirect_to: redirectTo }),
    })

    const data = await response.json()
    if (!response.ok) throw new Error(data.error || 'Failed to generate sign-in link')
    return data.link
  }

  return { inviteUser, updateUserEmail, sendPasswordReset, generateSignInLink }
}
